import { api } from '@goaltree/backend/convex/_generated/api'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@goaltree/ui/components/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@goaltree/ui/components/sidebar'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import {
  ArrowLeftRightIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  ExternalLinkIcon,
  GiftIcon,
  GlobeIcon,
  InfoIcon,
  LifeBuoyIcon,
  LogOutIcon,
  MonitorIcon,
  MoonIcon,
  PaletteIcon,
  SettingsIcon,
  SparklesIcon,
  SunIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AccentRotateSwitch, AccentSwatches, useAccentPicker } from '@/components/accent-swatches'
import { CanopyAvatar } from '@/components/canopy-avatar'
import { useSwitchMode, useSwitchModeStyle } from '@/components/coaching/coaching-mode'
import { InviteSheet } from '@/components/invite-banner'
import { StreakFlame } from '@/components/streak-icons'
import { useTheme } from '@/components/theme-provider'
import { UpgradeDialog } from '@/components/upgrade-dialog'
import { UserAvatar } from '@/components/user-avatar'
import { useIsPro } from '@/hooks/use-is-pro'
import i18n, { LANG_STORAGE_KEY, SUPPORTED_LANGS } from '@/i18n'
import { authClient } from '@/lib/auth-client'
import { useIsCoach, useIsCoaching } from '@/lib/coaching-mode'
import { clearOfflineData } from '@/lib/offline-cache'
import { optimisticSetLocale } from '@/lib/prefs-optimistic'
import { clearSessionIndicator } from '@/lib/session-indicator'

const HELP_URL = 'mailto:hello@goaltree.ai?subject=Goaltree%20feedback'

const LANG_LABELS: Record<string, string> = {
  en: 'English',
  ja: '日本語',
  es: 'Español',
  fr: 'Français',
  pt: 'Português',
  zh: '中文',
}

export function NavUser() {
  const { isMobile } = useSidebar()
  const navigate = useNavigate()
  const user = useQuery(api.auth.getCurrentUser)
  const setLocale = useMutation(api.preferences.setLocale).withOptimisticUpdate(optimisticSetLocale)
  const { data: session } = authClient.useSession()
  const { t, i18n: i18nInstance } = useTranslation()
  const currentLang = i18nInstance.language
  const { theme, setTheme } = useTheme()
  const { isPro } = useIsPro()
  const [upgradeOpen, setUpgradeOpen] = useState(false)
  const accentPicker = useAccentPicker(() => setUpgradeOpen(true))
  const [inviteOpen, setInviteOpen] = useState(false)
  const prefs = useQuery(api.preferences.getMyPreferences)
  const handle = (prefs as { handle?: string } | undefined)?.handle
  const streak = useQuery(api.nodes.getUserStreakInfo)?.streak ?? 0
  const isCoach = useIsCoach()
  const coaching = useIsCoaching()
  const switchLook = useSwitchModeStyle()
  const switchMode = useSwitchMode()

  const imageUrl = session?.user?.image ?? user?.image ?? null

  function signOut() {
    authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          clearSessionIndicator()
          clearOfflineData()
          navigate({ to: '/auth/login' })
        },
      },
    })
  }

  // Always through the paywall, so every plan (monthly, annual, lifetime) is offered.
  function handleUpgrade() {
    setUpgradeOpen(true)
  }

  return (
    <>
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="pro" />
      <InviteSheet open={inviteOpen} onOpenChange={setInviteOpen} />
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <SidebarMenuButton
                  size="lg"
                  className="cursor-pointer aria-expanded:bg-muted group-data-[collapsible=icon]:justify-center"
                />
              }
            >
              <CanopyAvatar userId={user?._id}>
                <UserAvatar
                  image={imageUrl}
                  email={user?.email}
                  name={user?.name}
                  className="size-8 shrink-0 rounded-full object-cover"
                />
              </CanopyAvatar>
              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-medium">{user?.name ?? '...'}</span>
                <span className="truncate text-xs text-foreground/70">
                  {handle ? `@${handle}` : (user?.email ?? '')}
                  {streak > 0 && (
                    <>
                      {' · '}
                      <StreakFlame /> {streak}
                    </>
                  )}
                  {isPro && ` · ${t('settings.planPro')}`}
                </span>
              </div>
              <ChevronsUpDownIcon className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="cursor-pointer"
                  render={
                    <Link
                      to="/settings"
                      onClick={() =>
                        sessionStorage.setItem('settings_from', window.location.pathname)
                      }
                    />
                  }
                >
                  <SettingsIcon className="size-4" />
                  {t('nav.settings')}
                </DropdownMenuItem>

                {/* Language */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <GlobeIcon className="size-4" />
                    {t('settings.language')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {SUPPORTED_LANGS.map((lang) => (
                      <DropdownMenuItem
                        key={lang}
                        className="cursor-pointer"
                        onClick={() => {
                          i18n.changeLanguage(lang)
                          localStorage.setItem(LANG_STORAGE_KEY, lang)
                          void setLocale({ locale: lang })
                        }}
                      >
                        <CheckIcon
                          className={`size-4 shrink-0 ${currentLang === lang ? 'opacity-100' : 'opacity-0'}`}
                        />
                        {LANG_LABELS[lang]}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>

                {/* Theme + Accent */}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger className="cursor-pointer">
                    <PaletteIcon className="size-4" />
                    {t('settings.theme')}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="min-w-44">
                    <DropdownMenuItem className="cursor-pointer" onClick={() => setTheme('light')}>
                      <SunIcon className="size-4 shrink-0" />
                      <span>{t('settings.themeLight')}</span>
                      {theme === 'light' && <CheckIcon className="ml-auto size-4" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => setTheme('dark')}>
                      <MoonIcon className="size-4 shrink-0" />
                      <span>{t('settings.themeDark')}</span>
                      {theme === 'dark' && <CheckIcon className="ml-auto size-4" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="cursor-pointer" onClick={() => setTheme('system')}>
                      <MonitorIcon className="size-4 shrink-0" />
                      <span>{t('settings.themeSystem')}</span>
                      {theme === 'system' && <CheckIcon className="ml-auto size-4" />}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="flex w-56 flex-col gap-1.5 px-2 py-1.5">
                      <AccentSwatches
                        {...accentPicker.swatches}
                        size={24}
                        className="flex-nowrap justify-between gap-1.5"
                      />
                      <AccentRotateSwitch {...accentPicker.rotateSwitch} />
                      <p className="text-[11px] leading-snug text-muted-foreground">
                        {accentPicker.line}
                      </p>
                    </div>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              </DropdownMenuGroup>

              {isCoach && (
                <DropdownMenuItem
                  onClick={switchMode}
                  className={`mt-1 cursor-pointer font-semibold ${switchLook.className}`}
                  style={switchLook.style}
                >
                  <ArrowLeftRightIcon className="size-4 text-current" />
                  {coaching
                    ? t('coachingMode.switchToPersonal')
                    : t('coachingMode.switchToCoaching')}
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <div className="flex items-center gap-2 px-2 py-2">
                  <SparklesIcon className="size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium">{t('settings.plan')}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${isPro ? 'bg-primary/10 text-primary-text' : 'bg-muted text-muted-foreground'}`}
                    >
                      {isPro ? t('settings.planPro') : t('settings.planFree')}
                    </span>
                    {!isPro && (
                      <button
                        type="button"
                        onClick={handleUpgrade}
                        className="inline-flex cursor-pointer items-center gap-1 rounded-full bg-primary px-2.5 py-1 text-[10px] font-bold text-white dark:text-[#0a0a0a] transition-all hover:bg-primary/90 disabled:opacity-50"
                      >
                        <SparklesIcon className="size-2.5" />
                        {t('settings.upgrade')}
                      </button>
                    )}
                  </div>
                </div>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() =>
                    window.open('https://goaltree.ai/?from=app#how-it-works', '_blank')
                  }
                >
                  <InfoIcon className="size-4" />
                  {t('settings.learnMore')}
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuItem className="cursor-pointer" onClick={() => setInviteOpen(true)}>
                  <GiftIcon className="size-4" />
                  {t('nav.inviteFriend')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => window.open(HELP_URL, '_self')}
                >
                  <LifeBuoyIcon className="size-4" />
                  {t('nav.help')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="cursor-pointer"
                  onClick={() => window.open('https://goaltree.ai/?from=app', '_blank', 'noopener')}
                >
                  <GlobeIcon className="size-4" />
                  {t('nav.website')}
                  <ExternalLinkIcon className="ml-auto size-3.5 text-muted-foreground" />
                </DropdownMenuItem>
              </DropdownMenuGroup>

              <DropdownMenuSeparator />

              <DropdownMenuGroup>
                <DropdownMenuItem
                  className="cursor-pointer text-muted-foreground focus:bg-destructive/10 focus:text-destructive focus:**:text-destructive!"
                  onClick={signOut}
                >
                  <LogOutIcon className="size-4" />
                  {t('settings.signOut')}
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  )
}
